
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import useTranslation from "next-translate/useTranslation";
import { getInitialStateFromQuery, queryParamsToApiInputVariables, SearchProvider } from "../../components/search/SearchContext";
import Layout from "../../components/Layout";
import Head from "next/head";
import SearchPage from "../../components/search/SearchPage";
import { CountryServiceType, Dog, SearchResult } from "../../interfaces";
import ErrorPage from "next/error";
import { Box } from "rebass/styled-components";
import OtherCities from "../../components/sitterprofile/OtherCities";
import serviceRoutes from "../../stories/fixtures/serviceRoutes.json";
import BreadcrumbList from "../../components/common/BreadcrumbList";
import { MetaContext } from "../../components/meta";
import { useContext, ReactElement, useState } from "react";
import { LocaleContext } from "../../components/locale";
import canonical from "../../lib/canonical";
import { GetServerSideProps } from "next";
import { SearchCityServiceTypeDocument, useSearchCityServiceTypeQuery } from "../../generated/graphql";
import { addApolloState, initializeApollo } from "../../lib/apolloClient";
import { useRouter } from "next/router";
import { trackOwnerSitterSearched } from "../../lib/event-tracking";
import { useIsMobile, withDeviceType } from "../../lib/media";
interface Props {
    query: Record<string, any>;
    statusCode: number;
    deviceType?: string;
    host: string;
    path: string;
}
interface SearchProps {
    query: Record<string, any>;
    countryServiceTypes: CountryServiceType[];
    userDogs: Dog[];
    searchResults: SearchResult;
    loading: boolean;
    error: any;
    fetchMore: any;
    h1Heading: string;
    subHeading: string;
    h2Heading: string;
    breadcrumb: ReactElement;
    mobileMapVisible: boolean;
    setMobileMapVisible: React.Dispatch<React.SetStateAction<boolean>>;
    isMobileViewport: boolean;
}
function Search(props: SearchProps) {
    const { query, countryServiceTypes, searchResults } = props;
    const initialSearchContextState = getInitialStateFromQuery({
        ...query,
        serviceTypeId: searchResults.serviceType.id,
        pid: searchResults.city.placeId,
        lat: searchResults.city.latitude,
        lng: searchResults.city.longitude,
        near: searchResults.city.name
    }, countryServiceTypes);
    return (<SearchProvider initialStateFromQuery={initialSearchContextState}>
      <SearchPage {...props} isSEOLanding/>
    </SearchProvider>);
}
function CityService(props: Props) {
    const { t } = useTranslation("search/cityservice");
    const { url } = useContext(MetaContext);
    const { country } = useContext(LocaleContext);
    const [mobileMapVisible, setMobileMapVisible] = useState(false);
    const { query } = useRouter();
    const { statusCode } = props;
    const isMobileViewport = useIsMobile(props.deviceType);
    const searchInputVariables = queryParamsToApiInputVariables(query);
    const { loading, error, data, fetchMore } = useSearchCityServiceTypeQuery({
        variables: {
            ...searchInputVariables,
            service: serviceRoutes[query.service],
            includePrice: true
        },
        onCompleted: data => {
            trackOwnerSitterSearched(searchInputVariables, data.searchCityServiceType, !!data.searchCityServiceType?.caregivers[0]?.bookingEstimation);
        }
    });
    const countryServiceTypes = data && data.country?.countryServiceTypes;
    const userDogs = data && data.currentUser ? data.currentUser.dogs : [];
    const searchResults = data && data.searchCityServiceType ? data.searchCityServiceType : null;
    const title = searchResults?.metaTitle || !searchResults?.city?.minServicePriceCents
        ? t(`titleServiceTypeId${searchResults?.serviceType?.id}Default`, {
            city: searchResults?.city?.name || ""
        })
        : t(`titleServiceTypeId${searchResults.serviceType?.id}`, {
            city: searchResults.city?.name || "",
            minServicePrice: parseInt(searchResults?.city?.minServicePriceCents! || "1000") / 100
        });
    const description = searchResults
        ? searchResults.metaDescription ||
            t(`descriptionServiceTypeId${searchResults.serviceType?.id}`, {
                city: searchResults.city ? searchResults.city.name! : ""
            })
        : null;
    if (statusCode == 404) {
        return <ErrorPage statusCode={404}/>;
    }
    const breadcrumb = searchResults && (<BreadcrumbList breadcrumbs={[
            {
                name: t("gudog"),
                href: "/",
                position: "1"
            },
            {
                name: t(`serviceTypeId${searchResults.serviceType?.id}`),
                href: {
                    pathname: "/search/service",
                    query: {
                        service: process.env.serviceRoutes[country][searchResults.serviceType.routeServiceParam].toString()
                    }
                },
                position: "2"
            }
        ]} activeItem={{
            name: t(`breadcrumbTitleServiceTypeId${searchResults.serviceType?.id}`, {
                city: searchResults ? searchResults.city?.name! : ""
            }),
            href: "/search/cityservice",
            position: "3"
        }}/>);
    const currency = searchResults?.city?.country?.currency;
    const minServicePrice = parseInt(searchResults?.city?.minServicePriceCents!) / 100;
    const maxServicePrice = parseInt(searchResults?.city?.maxServicePriceCents!) / 100;
    const servicePriceRange = `${minServicePrice}${currency} - ${maxServicePrice}${currency}`;
    const withAggregateRating = Number(searchResults?.city?.avgCaregiverReviewsScore) > 0;
    let sitterCount = searchResults?.city?.stats?.activeSitters ||
        searchResults?.totalEntries ||
        "...";
    switch (searchResults?.serviceType?.id) {
        case "1":
            sitterCount =
                searchResults?.city?.stats?.boardingActiveSitters ||
                    searchResults?.totalEntries ||
                    "...";
            break;
        case "2":
            sitterCount =
                searchResults?.city?.stats?.walkingActiveSitters ||
                    searchResults?.totalEntries ||
                    "...";
            break;
        case "7":
            sitterCount =
                searchResults?.city?.stats?.daycareActiveSitters ||
                    searchResults?.totalEntries ||
                    "...";
            break;
        default:
            break;
    }
    return (<Layout title={title!} description={description!} displayFooterAreas>
      <Head>
        <link rel="canonical" href={canonical(url!)}/>

        {searchResults?.city?.seoLandingNoindexTag && (<meta name="robots" content="noindex,follow"/>)}

        {searchResults?.richSnippets && (<div dangerouslySetInnerHTML={{ __html: searchResults?.richSnippets }}></div>)}

        {searchResults?.city?.country?.iso3661Alpha2 === "GB" ? (
        // only UK schema
        <script type="application/ld+json" dangerouslySetInnerHTML={{
                __html: `{
              "@context": "https://schema.org",
              "@type": "LocalBusiness",
              "name": "${t(`h1HeadingServiceTypeId${searchResults?.serviceType?.id}`, {
                    city: searchResults?.city?.name!
                }).split(".")[0]}",
              "description": "${t(`h1HeadingServiceTypeId${searchResults?.serviceType?.id}`, {
                    city: searchResults?.city?.name!
                }).split(".")[0]}",
              "url": "https://${props.host}${props.path}",
              "image": "https://${props.host}/images/logo.svg",
              "areaServed": {
                "@type": "Place",
                "name": "${searchResults?.city?.name!}",
                "geo": {
                  "@type": "GeoCoordinates",
                  "latitude": ${searchResults?.city?.latitude},
                  "longitude": ${searchResults?.city?.longitude}
                }
              },
              "currenciesAccepted": "${searchResults?.city?.country?.currency}",
              "paymentAccepted": "Credit Card",
              "priceRange": "${servicePriceRange}",
              ${withAggregateRating
                    ? `"aggregateRating": {
                "@type": "AggregateRating",
                "ratingValue": ${searchResults?.city?.avgCaregiverReviewsScore},
                "reviewCount": ${searchResults?.city?.sumCaregiverReviewsCount},
                "bestRating": 5,
                "worstRating": 1
              },`
                    : ""}
              "brand": {
                "@type": "Organization",
                "name": "Gudog",
                "logo": "https://${props.host}/images/logo.svg",
                "image": "https://${props.host}/images/logo.svg",
                "url": "https://${props.host}"
              }
            }`
            }}/>) : (<script type="application/ld+json" dangerouslySetInnerHTML={{
                __html: `{
              "@context": "https://schema.org",
              "@type": "Service",
              "serviceType": "${t(`h1HeadingServiceTypeId${searchResults?.serviceType?.id}`, {
                    city: searchResults?.city?.name!
                })}",
            "description": "${t(`schemaDescriptionServiceTypeId${searchResults?.serviceType?.id}`)}",
              "provider": {
                "@type": "LocalBusiness",
                "name": "Gudog",
                "url": "https://${props.host}${props.path}",
                ${withAggregateRating
                    ? `"aggregateRating": {
                  "@type": "AggregateRating",
                  "ratingValue": ${searchResults?.city?.avgCaregiverReviewsScore},
                  "reviewCount": ${searchResults?.city?.sumCaregiverReviewsCount},
                  "bestRating": 5,
                  "worstRating": 1
                },`
                    : ""}
                "areaServed": {
                  "@type": "Place",
                  "name": "${searchResults?.city?.name!}",
                  "geo": {
                    "@type": "GeoCoordinates",
                    "latitude": ${searchResults?.city?.latitude},
                    "longitude": ${searchResults?.city?.longitude}
                  }
                },
                "currenciesAccepted": "${searchResults?.city?.country?.currency}",
                "paymentAccepted": "Credit Card",
                "priceRange": "${servicePriceRange}"
              },
              "brand": {
                "@type": "Organization",
                "name": "Gudog",
                "logo": "https://${props.host}/images/logo.svg",
                "url": "https://${props.host}"
              },
              "image": "https://${props.host}/images/logo.svg"
            }`
            }}/>)}
      </Head>

      {searchResults && searchResults.serviceType && searchResults.city && (<Box>
          <Search countryServiceTypes={countryServiceTypes} userDogs={userDogs} searchResults={searchResults} loading={loading} error={error} fetchMore={fetchMore} h1Heading={searchResults?.title ||
                t(`h1HeadingServiceTypeId${searchResults.serviceType.id}`, {
                    city: searchResults.city.name!
                })} subHeading={searchResults?.subtitle ||
                t(`subHeadingServiceTypeId${searchResults.serviceType.id}`, {
                    city: searchResults.city.name!
                })} h2Heading={t(`h2HeadingServiceTypeId${searchResults.serviceType.id}`, {
                sittersCount: searchResults?.totalEntries || "...",
                city: searchResults.city.name!
            })} breadcrumb={breadcrumb} mobileMapVisible={mobileMapVisible} setMobileMapVisible={setMobileMapVisible} isMobileViewport={isMobileViewport} {...props}/>

          {!mobileMapVisible && (<Box variant="layout.large" pb={5}>
              <OtherCities title={searchResults.seoTitle!} contentHtml={searchResults.seoHtmlContent!} introHtml={searchResults.seoHtmlIntro!} serviceType={searchResults.serviceType} topCities={searchResults.topCities} city={searchResults.city} query={query} showCityServiceDescription blogPosts={searchResults.blogPosts} showHowItWorksAndBenefits/>
            </Box>)}
        </Box>)}
    </Layout>);
}
const getServerSideProps: GetServerSideProps = withDeviceType()(async (context) => {
    const { query, locale, req, res } = context;
    res.setHeader("Cache-Control", "public, s-maxage=60, stale-while-revalidate=3600");
    const apolloClient = initializeApollo(locale);
    const result = await apolloClient.query({
        query: SearchCityServiceTypeDocument,
        variables: {
            ...queryParamsToApiInputVariables(query),
            service: serviceRoutes[query.service],
            includePrice: true
        }
    });
    if (!result.data.searchCityServiceType ||
        result.data.searchCityServiceType.city.country.locale != locale) {
        return {
            notFound: true
        };
    }
    return addApolloState(apolloClient, {
        props: {
            host: req.headers.host,
            path: req.url,
            locale,
            query
        }
    });
});
export default CityService;

    async function __Next_Translate__getServerSideProps__1980d144697__(ctx) {
      const res = await getServerSideProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/search/cityservice',
            loaderName: 'getServerSideProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getServerSideProps__1980d144697__ as getServerSideProps }
  